import React from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';

import Flyout from '../flyout/flyout';
import Option from '../option/option';
import NDButton from '../button/button';

const TYPEAHEAD_RESET_MS = 750;

class Select extends React.Component {
  constructor(props) {
    super(props);

    this.typeaheadResetTimer = null;
    this.scrollFrame = null;
    this.listRef = React.createRef();
    this.containerRef = React.createRef();
    this.suppressTriggerFocusOpen = false;

    this.state = {
      selectedOption: this.props.defaultOption,
      showFlyout: false,
      highlightedIndex: null,
      typedQuery: '',
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.onClear
      && prevProps.defaultOption !== this.props.defaultOption
      && this.state.selectedOption !== this.props.defaultOption
    ) {
      this.setState({ selectedOption: this.props.defaultOption });
      return;
    }

    const highlightedIndexChanged = prevState.highlightedIndex !== this.state.highlightedIndex;
    const flyoutOpened = !prevState.showFlyout && this.state.showFlyout;
    const flyoutClosed = prevState.showFlyout && !this.state.showFlyout;

    if ((highlightedIndexChanged || flyoutOpened) && this.state.highlightedIndex !== null) {
      this.deferScrollHighlightedOptionIntoView();
    }

    // Mirrors the AngularJS dropdown-menu component's pattern of only listening for
    // outside clicks while the menu is open, so state changes from ANY path (toggle,
    // typeahead, escape, tab, option selection) always keep the listener in sync.
    if (flyoutOpened) {
      this.addOutsideClickListener();
    } else if (flyoutClosed) {
      this.removeOutsideClickListener();
    }
  }

  componentWillUnmount() {
    this.clearTypeaheadResetTimer();
    this.clearScrollFrame();
    this.removeOutsideClickListener();
  }

  onKeyDown = (e) => {
    const { highlightedIndex } = this.state;
    switch (e.keyCode) {
      case 38:
        e.preventDefault();
        this.onUpArrowPress();
        break;
      case 40:
        e.preventDefault();
        this.onDownArrowPress();
        break;
      case 9:
        if (highlightedIndex !== null) {
          this.onSelectionPress();
        } else {
          this.closeFlyout();
        }
        break;
      case 13:
        this.onSelectionPress();
        break;
      case 8:
        e.preventDefault();
        this.onBackspacePress();
        break;
      case 27:
        e.preventDefault();
        this.onEscapePress();
        break;
      default:
        if (this.isPrintableKey(e)) {
          e.preventDefault();
          this.onTypeaheadKeyPress(this.getPrintableKey(e));
        }
        break;
    }
  };

  getPrintableKey = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) {
      return '';
    }

    if (typeof e.key === 'string' && /^[a-z ]$/i.test(e.key)) {
      return e.key.toLowerCase();
    }

    if (e.keyCode === 32) {
      return ' ';
    }

    if (e.keyCode >= 65 && e.keyCode <= 90) {
      return String.fromCharCode(e.keyCode).toLowerCase();
    }

    return '';
  }

  isPrintableKey = e => !!this.getPrintableKey(e)

  normalizeOptionLabel = option => String(option?.display_name || option?.value || '').trim().toLowerCase()

  findMatchingIndex = (query) => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery.length) {
      return null;
    }

    return this.props.options.findIndex(option => this.normalizeOptionLabel(option).startsWith(normalizedQuery));
  }

  clearTypeaheadResetTimer = () => {
    if (this.typeaheadResetTimer) {
      clearTimeout(this.typeaheadResetTimer);
      this.typeaheadResetTimer = null;
    }
  }

  scheduleTypeaheadReset = () => {
    this.clearTypeaheadResetTimer();
    this.typeaheadResetTimer = setTimeout(() => {
      this.setState({ typedQuery: '' });
      this.typeaheadResetTimer = null;
    }, TYPEAHEAD_RESET_MS);
  }

  updateTypeahead = (nextQuery) => {
    const matchingIndex = this.findMatchingIndex(nextQuery);

    this.setState(prevState => ({
      showFlyout: prevState.showFlyout || nextQuery.length > 0,
      typedQuery: nextQuery,
      highlightedIndex: matchingIndex === -1 ? null : matchingIndex,
    }));

    if (nextQuery.length) {
      this.scheduleTypeaheadReset();
    } else {
      this.clearTypeaheadResetTimer();
    }
  }

  onTypeaheadKeyPress = (character) => {
    const { typedQuery } = this.state;
    this.updateTypeahead(`${typedQuery}${character}`);
  }

  onBackspacePress = () => {
    const { typedQuery } = this.state;
    this.updateTypeahead(typedQuery.slice(0, -1));
  }

  onEscapePress = () => {
    this.closeFlyout();
  }

  onSelectionPress = () => {
    const { highlightedIndex } = this.state;
    const { options } = this.props;
    if (highlightedIndex !== null) {
      this.handleOptionClick(options[highlightedIndex]);
    }
  }

  onUpArrowPress = () => {
    const { highlightedIndex } = this.state;
    const { options } = this.props;
    let newIndex = highlightedIndex;
    if (highlightedIndex && highlightedIndex !== 0) {
      newIndex = highlightedIndex - 1;
      this.setState({ highlightedIndex: newIndex });
    } else {
      newIndex = options.length - 1;
      this.setState({ highlightedIndex: newIndex });
    }
  }

  onDownArrowPress = () => {
    const { highlightedIndex } = this.state;
    const { options } = this.props;
    let newIndex = highlightedIndex;
    const length = options.length - 1;
    if (highlightedIndex !== null && highlightedIndex < length) {
      newIndex = highlightedIndex + 1;
      this.setState({ highlightedIndex: newIndex });
    } else {
      newIndex = 0;
      this.setState({ highlightedIndex: newIndex });
    }
  }

  addOutsideClickListener = () => {
    document.addEventListener('click', this.handleOutsideClick);
  }

  removeOutsideClickListener = () => {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  handleOutsideClick = (event) => {
    if (this.containerRef.current && !this.containerRef.current.contains(event.target)) {
      this.closeFlyout();
    }
  }

  handleOptionClick = (option) => {
    if (option !== this.state.selectedOption) {
      this.setState({ selectedOption: option });
      this.props.onChange(option);
    }
    this.closeFlyout();
  }

  toggleFlyout = () => {
    // mousedown fires before the browser's own focus event, so mark this focus
    // as already accounted for and prevent onTriggerFocus from unconditionally
    // reopening a flyout this same click just closed.
    this.suppressTriggerFocusOpen = true;
    if (this.state.showFlyout) {
      this.closeFlyout();
    } else {
      this.openFlyout();
    }
  }

  onTriggerFocus = () => {
    if (this.suppressTriggerFocusOpen) {
      this.suppressTriggerFocusOpen = false;
      return;
    }
    this.openFlyout();
  }

  openFlyout = () => {
    this.setState({ showFlyout: true });
  }

  closeFlyout = () => {
    this.clearTypeaheadResetTimer();
    this.setState({
      showFlyout: false,
      highlightedIndex: null,
      typedQuery: '',
    });
  }

  scrollHighlightedOptionIntoView = () => {
    const listNode = this.listRef.current;

    if (!listNode) {
      return;
    }

    const highlightedOption = listNode.querySelector('[data-highlighted="true"], .option-button.highlighted');

    if (highlightedOption && typeof highlightedOption.scrollIntoView === 'function') {
      highlightedOption.scrollIntoView({ block: 'nearest' });
    }
  }

  clearScrollFrame = () => {
    if (this.scrollFrame !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  deferScrollHighlightedOptionIntoView = () => {
    this.clearScrollFrame();

    if (typeof requestAnimationFrame === 'function') {
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.scrollHighlightedOptionIntoView();
      });
      return;
    }

    this.scrollHighlightedOptionIntoView();
  }

  displayErrorState = () => {
    const {
      errorMessage,
    } = this.props;
    return (
      errorMessage ? <p className="error">{errorMessage}</p> : ''
    );
  }

  render() {
    const {
      defaultOption,
      dropdownIcon,
      options,
      errorMessage,
      className,
      disabled,
      showDefault,
      isMagnified,
      isHighlighted,
    } = this.props;

    const {
      selectedOption,
      highlightedIndex,
      showFlyout,
    } = this.state;

    const renderOptions = options.map((option, index) => {
      const isActive = option.value === selectedOption.value;
      return (
        <Option
          isMagnified={isMagnified}
          isActive={isActive}
          key={`${option.value}-${option.display_name}`}
          option={option}
          onClick={this.handleOptionClick}
          isHighlighted={index === highlightedIndex}
        />
      );
    });

    // the currentValue can be 3 things:
    // * the currently selected value
    // * defaultOption.display_name (if the defaultOption is an {})
    // * or defaultOption as a string. see propTypes for addtional info
    //
    // `selectedOption`/`defaultOption` are only ever a string OR an `{value, display_name}`
    // option object (never rendered directly) -- `optionLabel` enforces that even when an
    // option object's `display_name` is a legitimate empty string (e.g. the blank/"---------"
    // choice DRF returns for an unset ChoiceField, `{value: '', display_name: ''}`), so the
    // old `a.display_name || b.display_name || b` chain never falls all the way through to
    // rendering the raw option OBJECT itself, which crashed with "Objects are not valid as a
    // React child" (React error #31) for any field whose stored value was still blank. Applied
    // to the `showDefault` branch too, since every current caller happens to only pass a string
    // there today, but nothing enforces that.
    const optionLabel = option => (option && typeof option === 'object' ? option.display_name : option);
    const currentValue = showDefault ? optionLabel(defaultOption) : (optionLabel(selectedOption) || optionLabel(defaultOption));

    return (
      <div className={`${className} nd-select-component`} ref={this.containerRef}>
        <NDButton
          isMagnified={isMagnified}
          isHighlighted={isHighlighted}
          className="select-button"
          disabled={disabled}
          onMouseDown={this.toggleFlyout}
          onKeyDown={this.onKeyDown}
          onFocus={this.onTriggerFocus}
          dataE2e={this.props.dataE2e}
          variant="action"
          icon={dropdownIcon}
          iconPosition="right"
          width="full"
          invalid={!!errorMessage.length}
        >
          {currentValue || <span className="placeholder">Select</span>}
        </NDButton>
        <Flyout
          showFlyout={showFlyout}
        >
          <div className="select">
            <ul className="list-handler" ref={this.listRef}>
              {renderOptions}
            </ul>
          </div>
        </Flyout>
        {this.displayErrorState()}
      </div>
    );
  }
}

Select.propTypes = {
  className: PropTypes.string,
  onClear: PropTypes.bool,
  // in some cases, the defaultOption needs to be a string
  // because it is not an option that is a part of the
  // options list. ex: a snooze dropdown has a defaultOption
  // of "Remind Me" which is just a string because it is not
  // considered a valid Option for its use case
  defaultOption: PropTypes.oneOfType([
    PropTypes.shape({
      display_name: PropTypes.string,
      value: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  dropdownIcon: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    display_name: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  errorMessage: PropTypes.string,
  dataE2e: PropTypes.string,
  disabled: PropTypes.bool,
  showDefault: PropTypes.bool,
  isMagnified: PropTypes.bool,
  isHighlighted: PropTypes.bool,
};

Select.defaultProps = {
  className: '',
  defaultOption: '',
  dropdownIcon: 'chevron-down',
  onClear: false,
  errorMessage: '',
  dataE2e: '',
  disabled: false,
  showDefault: false,
  isMagnified: false,
  isHighlighted: false,
};

export default Select;

